import FJConstant from '../constants/FJConstant';
import FJCoreConfig from './FJCoreConfig.js';
import FJUtil from '../util/FJUtil';

class FJVideoPlayBase {
    _source = null; // 视频Video 标签
    _sourceInfo = null; // 视频源信息
    _sourceUrl = null; // 视频源url

    _sourceBlob = null; // 视频源blob

    // 状态  FJConstant.LOADING 加载中 FJConstant.ERROR 加载错误 FJConstant.SUCCESS 加载成功
    _loadStatus = FJConstant.DEFAULT;
    _isPlaying = false;
    _duration = 0;

    /** @type {((loading: boolean) => void) | null} */
    _bufferingListener = null;
    _bufferingOnWaiting = null;
    _bufferingOnPlaying = null;

    /** 取消上一轮尚未完成的 seek（避免快速连续 seek 时旧定时器 10s 后误报「seeked 超时」） */
    _pendingSeekAbort = null;

    constructor() {}

    /**
     * @param {((loading: boolean) => void) | null} fn
     */
    setBufferingListener(fn) {
        this._removeBufferingListeners();
        this._bufferingListener = typeof fn === 'function' ? fn : null;
        this._attachBufferingListeners();
    }

    _removeBufferingListeners() {
        if (!this._source || !this._bufferingOnWaiting) return;
        this._source.removeEventListener('waiting', this._bufferingOnWaiting);
        this._source.removeEventListener('playing', this._bufferingOnPlaying);
        this._bufferingOnWaiting = null;
        this._bufferingOnPlaying = null;
    }

    _attachBufferingListeners() {
        if (!this._source || !this._bufferingListener) return;
        this._bufferingOnWaiting = () => this._bufferingListener(true);
        this._bufferingOnPlaying = () => this._bufferingListener(false);
        this._source.addEventListener('waiting', this._bufferingOnWaiting);
        this._source.addEventListener('playing', this._bufferingOnPlaying);
    }

    /**
     * @desc 跳转到指定帧
     * @param {Number} time
     */
    async seek(time, callback) {
        if (!this._source) return;
        const video = this._source;
        const t = Math.max(0, Number(time) || 0);

        /**
         * `getCoverThumbnail` 等处会 `await seek`：若仅依赖 `seeked` 事件 resolve Promise，
         * 在「目标时刻与当前 currentTime 几乎相同」时，部分浏览器不会再触发 `seeked`，
         * 导致 Promise 永远挂起。先短路可避免卡死。
         */
        const SAME_TIME_EPS_SEC = 0.02;
        if (Math.abs(video.currentTime - t) < SAME_TIME_EPS_SEC) {
            callback && callback();
            return;
        }

        if (this._pendingSeekAbort) {
            this._pendingSeekAbort();
            this._pendingSeekAbort = null;
        }

        /** 极端情况下仍无 seeked（解码器异常等），避免永久 await */
        const SEEK_TIMEOUT_MS = 10000;

        const seeked = await new Promise(resolve => {
            let settled = false;
            let timeoutId;
            /** @type {() => void} */
            let seekedHandler;

            const finish = ok => {
                if (settled) return;
                settled = true;
                clearTimeout(timeoutId);
                video.removeEventListener('seeked', seekedHandler);
                this._pendingSeekAbort = null;
                resolve(ok);
            };

            seekedHandler = () => finish(true);

            timeoutId = setTimeout(() => {
                console.warn('[FJVideoPlayBase] seek: seeked 超时，使用当前帧继续');
                finish(true);
            }, SEEK_TIMEOUT_MS);

            this._pendingSeekAbort = () => {
                clearTimeout(timeoutId);
                video.removeEventListener('seeked', seekedHandler);
                if (!settled) {
                    settled = true;
                    this._pendingSeekAbort = null;
                    resolve(false);
                }
            };

            video.addEventListener('seeked', seekedHandler);
            video.currentTime = t;

            if (this._isPlaying) {
                this._playTime = t;
            }
        });

        if (seeked) {
            callback && callback();
        }
    }

    /**
     * @desc 绘制画面
     */
    async draw(viewCtx, rect) {
        if (!viewCtx || this._loadStatus !== FJConstant.SUCCESS || !this._source) return;

        viewCtx.drawImage(this._source, rect.x, rect.y, rect.width, rect.height);
    }

    /**
     * @desc 播放视频
     */
    async play(callback, pauseCallback) {
        if (this._isPlaying) return;
        if (!this._source) return;

        this._playTime = this._source.currentTime;
        try {
            /** `await seek()` 若在用户点击之后再执行，会使 play() 脱离手势栈；静音策略见 {@link FJVideoElement.init} 共享分支 */
            await this._source.play();
        } catch (e) {
            console.warn('[FJVideoPlayBase] play() 被拒绝（常见于未静音时的异步手势失效）', e);
            throw e;
        }
        this._isPlaying = true;

        if (this._source) {
            /** 按工程 FPS（默认 30）节拍触发回调与绘制，避免跟随显示器刷新率 */
            const frameMs = 1000 / FJCoreConfig.FPS;
            let acc = 0;
            let lastTs;

            const __playing = timestamp => {
                if (lastTs === undefined) lastTs = timestamp;
                acc += timestamp - lastTs;
                lastTs = timestamp;

                const src = this._source;
                if (!src || !this._isPlaying) return;

                if (src.currentTime >= this._duration - 1e-3) {
                    void this.pause();
                    pauseCallback && pauseCallback(src.currentTime);
                    return;
                }

                if (acc >= frameMs) {
                    acc -= frameMs;
                    this._playTime = src.currentTime;
                    if (this._playTime >= 0 && this._playTime <= this._duration) {
                        callback && callback(this._playTime);
                        this.draw();
                    }
                }

                if (this._isPlaying) {
                    this._requestTimer = window.requestAnimationFrame(__playing);
                }
            };

            if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
            this._requestTimer = window.requestAnimationFrame(__playing);
        }
    }

    /**
     * @desc 暂停视频
     */
    async pause() {
        if (!this._isPlaying || !this._source) return;

        if (this._requestTimer) window.cancelAnimationFrame(this._requestTimer);
        await this._source.pause();
        this._isPlaying = false;
    }

    /**
     * @desc 设置音量
     * @param {Number} volume
     */
    setVolume(volume) {
        this._source.volume = volume;
    }

    /**
     * @desc 设置视频源
     * @param {Object} source
     */
    async setSource(src) {
        try {
            this._removeBufferingListeners();
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            video.preload = 'auto';
            this._loadStatus = FJConstant.LOADING;
            this._sourceUrl = src;

            const sourceInfo = await new Promise((resolve, reject) => {
                const __onCanplay = () => {
                    if (video.duration === Infinity) {
                        video.currentTime = 10000;
                    } else {
                        __onSuccess();
                    }
                };

                const __onSuccess = () => {
                    const fileData = {
                        width: video.videoWidth,
                        height: video.videoHeight,
                    };
                    this._duration = video.duration;
                    this._loadStatus = FJConstant.SUCCESS;

                    video.removeEventListener('seeked', __onSuccess);
                    video.removeEventListener('canplay', __onCanplay);
                    video.removeEventListener('error', __onError);

                    resolve(fileData);
                };

                const __onError = () => {
                    this._loadStatus = FJConstant.ERROR;
                    video.removeEventListener('seeked', __onSuccess);
                    video.removeEventListener('canplay', __onCanplay);
                    video.removeEventListener('error', __onError);
                    reject();
                };

                video.addEventListener('seeked', __onSuccess);
                // 监听元数据加载完成事件
                video.addEventListener('canplay', __onCanplay);
                video.addEventListener('error', __onError);

                video.src = src;
            });

            this._source = video;
            this._sourceInfo = sourceInfo;
            this._attachBufferingListeners();
        } catch (error) {
            this._loadStatus = FJConstant.ERROR;
            throw error;
        }
    }
    setSourceBlob(blob, info) {
        this._sourceBlob = blob;
        this._sourceInfo = info;
    }

    clearAll() {
        this.pause();
        this._removeBufferingListeners();
        this._source = null;
        this._sourceInfo = null;
        this._loadStatus = FJConstant.DEFAULT;
        this._isPlaying = false;
        this._duration = 0;
        this._sourceBlob = null;
    }

    /**
     * @desc 获取视频源信息
     * @returns {Object}
     */
    getSourceInfo() {
        if (!this._sourceInfo) return null;
        return {
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
            duration: this._duration,
        };
    }

    getCurrentTime() {
        if (!this._source) return 0;
        return this._source.currentTime;
    }

    getIsPlaying() {
        return this._isPlaying;
    }

    getSize() {
        if (!this._sourceInfo) return null;
        return {
            width: this._sourceInfo.width,
            height: this._sourceInfo.height,
        };
    }

    getSourceStatus() {
        return this._loadStatus;
    }

    /**
     * @desc 获取视频源blob
     * @returns {Promise<Blob>}
     */
    async getSourceBlob() {
        if (this._sourceBlob) return this._sourceBlob;
        const blob = await FJUtil.getBlobByUrl(this._sourceUrl, true);
        this._sourceBlob = blob;
        return blob;
    }

    getSourceUrl() {
        return this._source.src;
    }
    getSource() {
        return this._source;
    }
}

export default FJVideoPlayBase;
